/**
 * Per-account inbound-SMS notification options (office_manager+):
 *   - email the account when a text arrives (to contact email or override)
 *   - forward every inbound text to a phone (sent from the receiving DID)
 *
 * Stored in customers.settings -> 'sms_notifications' (migration 0117
 * work; no schema change — reuses the account settings JSON).
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sms } from '@/api/sms';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PhoneInput } from '@/components/PhoneInput';
import { BellRing } from 'lucide-react';

export function SmsNotificationSettingsCard() {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();

  const { data, isLoading } = useQuery({
    queryKey: ['sms-notification-settings', customerId],
    queryFn: () => sms.getNotificationSettings({
      customer_id: customerId ?? undefined,
    }),
  });

  const [notifyEmail, setNotifyEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [forward, setForward] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!data) return;
    setNotifyEmail(data.notify_email);
    setEmailTo(data.notify_email_to ?? '');
    setForward(data.forward_e164 ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: () => sms.saveNotificationSettings({
      notify_email: notifyEmail,
      notify_email_to: emailTo.trim() || null,
      forward_e164: forward.trim() || null,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-notification-settings'] });
      setError(null);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  const dirty = data != null && (
    notifyEmail !== data.notify_email
    || (emailTo.trim() || null) !== data.notify_email_to
    || (forward.trim() || null) !== data.forward_e164
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm inline-flex items-center gap-2">
          <BellRing className="h-4 w-4 text-muted-foreground" />
          Inbound message notifications
        </CardTitle>
        <CardDescription>
          What happens when someone texts one of this account&rsquo;s numbers —
          besides landing in the portal inbox.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 max-w-md">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="sms-notify-email" className="text-sm">Email me each text</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sends the message body to the address below.
                </p>
              </div>
              <Switch
                id="sms-notify-email"
                checked={notifyEmail}
                onCheckedChange={setNotifyEmail}
              />
            </div>

            {notifyEmail && (
              <div className="space-y-1">
                <Label htmlFor="sms-notify-email-to">Send to</Label>
                <Input
                  id="sms-notify-email-to"
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder={data?.contact_email ?? 'account contact email'}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the account contact email
                  {data?.contact_email ? ` (${data.contact_email})` : ''}.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="sms-forward">Forward texts to a phone (optional)</Label>
              <PhoneInput
                id="sms-forward"
                value={forward}
                onChange={setForward}
                inputClassName="font-mono"
                customerId={customerId ?? undefined}
              />
              <p className="text-xs text-muted-foreground">
                Every inbound text is re-sent to this number from the number
                that received it. Clear the field to stop forwarding.
              </p>
            </div>

            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <div className="flex items-center gap-3">
              <Button
                type="button" size="sm"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save notifications'}
              </Button>
              {savedFlash && (
                <span className="text-xs font-medium text-emerald-600" role="status">Saved</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
